import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  deactivateUser,
  reactivateUser,
  updateUser,
  resettingPassword,
} from "../redux/actions";
import { Controller, useForm } from "react-hook-form";
import {
  TextField,
  Grid,
  Paper,
  Avatar,
  Box,
  makeStyles,
} from "@material-ui/core";
import { Delete, Lock, RestoreFromTrash } from "@material-ui/icons";
import { teamsService, titlesService } from "../services";
import { User as UserModelType, UserType } from "../services/domain/User";
import { UserRole } from "../services/domain/userRole";
import { Select } from "@ofqual/portal-components/components/Select";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { roles } from "./roleHelper";
import { Error } from "./common/Error";
import { UsersAppBar } from "./UsersAppBar";
import {
  AppBarSaveButton,
  AppBarConfirmButton,
} from "@ofqual/portal-components/components/SecondaryAppBar";
import { useLookup } from "@ofqual/portal-core/core/hooks";

import { UserState, UserStatus } from "../redux/state";
import {
  UserViewModel,
  defaultValues,
  toViewModel,
  fromViewModel,
} from "./viewModels/UserViewModel";
import { validationHelper } from "./common/validationHelper";
import { authSelector } from "../redux/selectors";

const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const useStyles = makeStyles((theme) => {
  return {
    section: {
      margin: 10,
      marginBottom: 30,
      display: "flex",
      alignItems: "center",
    },
  };
});

export const UserForm: React.FC<{ userState: UserState }> = ({ userState }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { upn } = useParams<{ upn: string }>();

  const { isInRole } = useAuth(authSelector);

  const {
    register,
    handleSubmit,
    errors,
    control,
    formState,
    reset,
    setError,
  } = useForm<UserViewModel>({
    defaultValues,
  });

  const user: UserModelType | null =
    (userState?.user && userState.user.upn === upn && userState.user) || null;

  useEffect(() => {
    if (user) {
      // once user is available in redux, load it in to the form via the `reset` function
      reset(toViewModel(user));
    }
  }, [user, reset]);

  const { dirtyFields } = formState;

  const { lastCrudResult } = userState;

  useEffect(() => {
    validationHelper(lastCrudResult, (name, message) =>
      setError(name, { message })
    );
  }, [lastCrudResult, setError]);

  const onSubmit = (viewModel: UserViewModel) => {
    const jsonPatch = fromViewModel(
      viewModel,
      Object.keys(dirtyFields) as (keyof UserViewModel)[]
    );
    dispatch(updateUser(upn, jsonPatch));
  };

  const roleLookups = user
    ? roles[user.userType].roleLookups.map((item) => ({
        ...item,
        disabled: item.isDefault,
      }))
    : [];

  const isAuthedForEdit = isInRole(UserRole.ITAdmin, UserRole.AOAdmin);

  const isEditable =
    isAuthedForEdit && userState.status === "LOADED" && user && user.active;

  const isReactivatable =
    isAuthedForEdit && userState.status === "LOADED" && user && !user.active;

  const isResettable = isEditable && user?.userType !== UserType.Ofqual;

  const [titlesLookups, teamLookups] = useLookup(titlesService, {
    service: teamsService,
    id: "id",
    label: "name",
  });

  return (
    <div>
      {isAuthedForEdit && (
        <UsersAppBar breadcrumbs={[{ label: user?.upn || "" }]}>
          {user?.active ? (
            <AppBarConfirmButton
              tooltip="Deactivate"
              Icon={Delete}
              onClick={() => dispatch(deactivateUser(upn))}
              disabled={!isEditable}
            >
              Are you sure you want to deactivate user{" "}
              <strong>{user.upn}</strong>?
            </AppBarConfirmButton>
          ) : (
            <AppBarConfirmButton
              tooltip="Reactivate"
              Icon={RestoreFromTrash}
              onClick={() => dispatch(reactivateUser(upn))}
              disabled={!isReactivatable}
            >
              Are you sure you want to activate user{" "}
              <strong>{user?.upn}</strong>?
            </AppBarConfirmButton>
          )}
          &nbsp;
          <AppBarConfirmButton
            tooltip="Reset Password"
            Icon={Lock}
            onClick={() => dispatch(resettingPassword(upn))}
            disabled={!isResettable}
          >
            Are you sure you want to reset the password for{" "}
            <strong>{user?.upn}</strong>?
          </AppBarConfirmButton>
          &nbsp;
          <AppBarSaveButton
            isSubmitting={userState.status === UserStatus.updating}
            isSubmitted={userState.lastCrudResult.isSuccess}
            onClick={(event) => {
              handleSubmit(onSubmit)(event);
            }}
            disabled={!isEditable}
            dataTest="save-button"
          />
        </UsersAppBar>
      )}
      {!user ? null : (
        <Paper style={{ padding: 20 }}>
          <Grid container spacing={2} direction="row" alignItems="center">
            <Box className={classes.section}>
              <Grid item>
                <Avatar></Avatar>
              </Grid>
              <Grid item>
                &nbsp;&nbsp;&nbsp;
                {user.userType === UserType.Ofqual && "Ofqual"}
                {user.userType === UserType.CCEA && "CCEA Regulation"}
                {user.userType === UserType.AO &&
                  `AO - ${user?.awardingOrg?.name}`}
              </Grid>
              {user.active ? null : <Grid item>(Inactive)</Grid>}
            </Box>
          </Grid>
          <form>
            <Grid container spacing={3}>
              {user.userType === UserType.AO ? (
                <Grid item xs={12}>
                  <TextField
                    name="awardingOrgName"
                    inputRef={register}
                    variant="outlined"
                    label="Awarding organisation"
                    fullWidth
                    disabled
                  />
                </Grid>
              ) : null}
              {user.userType === UserType.AO ||
              user.userType === UserType.CCEA ? (
                <>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name="title"
                      rules={{ required: "This field is required" }}
                      as={
                        <Select
                          label="Title"
                          disabled={!isEditable}
                          data-test="user-title"
                        >
                          {titlesLookups}
                        </Select>
                      }
                    />
                    {errors.title && <Error>This field is required</Error>}
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      name="firstName"
                      inputRef={register}
                      variant="outlined"
                      label="First name"
                      fullWidth
                      disabled={!isEditable}
                    />
                    {errors.firstName && <Error>This field is required</Error>}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="surname"
                      inputRef={register({ required: true })}
                      variant="outlined"
                      label="Surname"
                      fullWidth
                      disabled={!isEditable}
                    />
                    {errors.surname && <Error>This field is required</Error>}
                  </Grid>
                </>
              ) : null}
              <Grid item xs={12}>
                <TextField
                  name="loginEmail"
                  inputRef={register}
                  variant="outlined"
                  label="Login email"
                  fullWidth
                  disabled
                />
              </Grid>
              {user.userType === UserType.AO ||
              user.userType === UserType.CCEA ? (
                <React.Fragment>
                  <Grid item xs={12}>
                    <TextField
                      name="alternateEmail"
                      inputRef={register({
                        required: true,
                        pattern: EMAIL_PATTERN,
                      })}
                      variant="outlined"
                      label="Email"
                      fullWidth
                      disabled={!isEditable}
                    />
                    {errors.alternateEmail && (
                      <Error>
                        {!!errors.alternateEmail.message
                          ? errors.alternateEmail.message
                          : "Please enter a valid email"}
                      </Error>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="telephoneNumber"
                      inputRef={register({ required: true })}
                      variant="outlined"
                      label="Phone number"
                      fullWidth
                      disabled={!isEditable}
                    />
                    {errors.telephoneNumber && (
                      <Error>This field is required</Error>
                    )}
                  </Grid>
                </React.Fragment>
              ) : null}

              {user.userType === UserType.Ofqual ? (
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="teamIds"
                    rules={{ validate: (value) => !!value.length }}
                    as={
                      <Select selectVariant="multiple" label="Team(s)">
                        {teamLookups}
                      </Select>
                    }
                  />
                  {errors.teamIds && <Error>This field is required</Error>}
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="userRoles"
                  rules={{ required: "This field is required" }}
                  as={
                    <Select selectVariant="multiple" label="User Role(s)">
                      {roleLookups}
                    </Select>
                  }
                />
                {errors.userRoles && <Error>This field is required</Error>}
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}
    </div>
  );
};
